import type {
  ImageReference,
  LeafPrimitive,
  SymbolDataStructure,
  Symbology,
  Template,
  TextValue,
} from '@tsagko/label-contracts'
import { flattenZones } from './flatten-zones'
import { canonicalImageRefKey } from './internal/image-ref-key'

/**
 * Legacy `ValueSource` shape carried by templates persisted before the
 * 2026-05-24 `TextValue` migration. See `internal/units.ts` for the full
 * compatibility story.
 */
type LegacyStatic = { kind: 'static'; value: string }
type LegacyBound = { kind: 'bound'; fieldKey: string }
type AnyValueSource = TextValue | LegacyStatic | LegacyBound

/**
 * Regex matching a TextValue content string that is a single `{name}`
 * reference (whitespace tolerated, no surrounding text). Same source as
 * `variables.ts#SINGLE_VARIABLE_RE` — duplicated rather than exported to
 * keep `variables.ts`'s public surface minimal.
 */
const SINGLE_VARIABLE_RE = /^\s*\{([a-zA-Z][a-zA-Z0-9_]*)\}\s*$/

/**
 * Extract the variable name referenced by a text-value carrier, or
 * `undefined` if it carries a pure literal / static value. Threads both
 * the new `TextValue { content }` shape and the legacy
 * `{ kind: 'bound', fieldKey }` shape — see `internal/units.ts`.
 */
function extractVariableName(source: AnyValueSource): string | undefined {
  if ('content' in source) {
    const m = SINGLE_VARIABLE_RE.exec(source.content)
    return m?.[1]
  }
  if (source.kind === 'bound') return source.fieldKey
  return undefined
}

/**
 * The four requirement buckets a template's renderer needs to satisfy before
 * a render call can succeed:
 *
 *   - `requiredFonts`   — every text-bearing leaf's `fontId` (defaulted to
 *                         `'0'` when omitted, which matches Zebra's built-in
 *                         font A default and the runtime's fallback path).
 *   - `requiredImages`  — canonical `ImageReference` objects for every image
 *                         leaf; deduped by `canonicalImageRefKey(ref)`
 *                         (sorted-keys JSON — insertion-order-independent).
 *   - `requiredSymbols` — every distinct `Symbology` encountered.
 *   - `requiredFields`  — every `fieldKey` referenced via either a
 *                         `ValueSource: { kind: 'bound' }` or an
 *                         `ImageReference: { kind: 'field' }`.
 *
 * Used by `generateZpl` to populate the render manifest's `usedFonts`,
 * `usedImages`, `usedSymbols` arrays (sub-task 2a.10.13), and by the demo
 * adapter (sub-task 2a.10.17) to surface "you need to bind X" prompts before
 * rendering.
 */
export interface TemplateRequirements {
  requiredFonts: string[]
  requiredImages: ImageReference[]
  requiredSymbols: Symbology[]
  requiredFields: string[]
}

/**
 * Walk `flattenZones(template)` (which expands baked-block-instances into
 * their leaf primitives) and collect everything the renderer needs to know
 * about ahead of time. Pure — does not mutate the input template.
 *
 * Per-leaf contributions:
 *
 *   - text / localized-text / localized-rich-text:
 *       fonts ← `component.fontId ?? '0'`
 *       fields ← every `{kind:'bound'}` value found inside `.value` (text)
 *                or `.variants[i].value` (localized variants)
 *
 *   - image:
 *       images ← `component.source` (deduped by JSON canonical form)
 *       fields ← `component.source.fieldKey` when `source.kind === 'field'`
 *
 *   - symbol:
 *       symbols ← `component.symbology`
 *       fields ← every `{kind:'bound'}` inside the dataStructure (1 value for
 *                raw/gtin/url, N values for gs1-ai elements)
 *
 *   - separator:
 *       no requirements contribution.
 *
 * Dedup strategy:
 *   - fonts, symbols, fields: tracked in `Set<string>`, emitted as arrays in
 *     insertion order. Insertion order is preserved by ES2015 Set semantics.
 *   - images: deduped via a parallel `Set<string>` keyed on
 *     `canonicalImageRefKey(ref)` (sorted-keys JSON serialization, so
 *     `{kind:'url',url:'x'}` and `{url:'x',kind:'url'}` produce the same key).
 *     The `requiredImages` array stores the actual `ImageReference` objects
 *     in first-seen order.
 *
 * Order: no sorting; first-seen wins. Callers that want a stable
 * presentation order should sort downstream.
 */
export function analyzeTemplateRequirements(template: Template): TemplateRequirements {
  const fonts = new Set<string>()
  const symbols = new Set<Symbology>()
  const fields = new Set<string>()
  const imageKeys = new Set<string>()
  const requiredImages: ImageReference[] = []

  const pushImage = (ref: ImageReference): void => {
    const key = canonicalImageRefKey(ref)
    if (!imageKeys.has(key)) {
      imageKeys.add(key)
      requiredImages.push(ref)
    }
  }

  const pushBound = (source: AnyValueSource): void => {
    // Only references to runtime data contribute to requiredFields. Pure
    // literals are self-contained and need no data binding. We intentionally
    // do NOT filter out empty references here — that's lint's job
    // (UNBOUND_REQUIRED_FIELD); analyzers surface the raw shape so callers
    // can decide whether to error or just collect the name.
    //
    // Variable-name semantics (new TextValue shape): a `{name}` content string
    // contributes the variable name. Legacy `{ kind: 'bound', fieldKey }`
    // contributes the fieldKey. Pure literals / legacy 'static' values
    // contribute nothing.
    const name = extractVariableName(source)
    if (name !== undefined) {
      fields.add(name)
    }
  }

  const walkSymbolStructure = (ds: SymbolDataStructure): void => {
    switch (ds.kind) {
      case 'raw':
      case 'gtin':
      case 'url':
        pushBound(ds.value as AnyValueSource)
        return
      case 'gs1-ai':
        for (const el of ds.elements) {
          pushBound(el.value as AnyValueSource)
        }
        return
      default: {
        // Exhaustiveness guard — TypeScript will fail if a new structure
        // kind is added without an arm above.
        const _exhaustive: never = ds
        void _exhaustive
      }
    }
  }

  for (const zone of flattenZones(template)) {
    const c: LeafPrimitive = zone.component
    switch (c.type) {
      case 'text':
        fonts.add(c.fontId ?? '0')
        pushBound(c.value as AnyValueSource)
        break
      case 'localized-text':
      case 'localized-rich-text':
        fonts.add(c.fontId ?? '0')
        for (const v of c.variants) {
          pushBound(v.value as AnyValueSource)
        }
        break
      case 'image':
        pushImage(c.source)
        if (c.source.kind === 'field') {
          fields.add(c.source.fieldKey)
        }
        break
      case 'symbol':
        symbols.add(c.symbology)
        walkSymbolStructure(c.dataStructure)
        break
      case 'separator':
        // No font / image / symbol / field contribution.
        break
      default: {
        const _exhaustive: never = c
        void _exhaustive
      }
    }
  }

  return {
    requiredFonts: [...fonts],
    requiredImages,
    requiredSymbols: [...symbols],
    requiredFields: [...fields],
  }
}
